import { QueryKey } from '@tanstack/query-core'
import {
  InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  mutationOptions,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import queryString from 'query-string'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { createSaveSelectedVirtualCardIdsAction } from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import {
  VirtualCardListModel,
  VirtualCardListResponse,
  VirtualCardSecureModel,
  VirtualCardSecureResponse,
  VirtualCardType,
} from 'src/models/virtualCard.models'
import {
  VirtualCardPermissionsModel,
  VirtualCardPermissionsResponse,
} from 'src/models/virtualCardPermissions.models'
import {
  GetVirtualCardRevisionsPagination,
  GetVirtualCardRevisionsQueryParams,
  VirtualCardRevisionListModel,
  VirtualCardRevisionListResponse,
} from 'src/models/virtualCardRevision.models'
import { closeModal } from 'src/navigation/Lnk'
import { QueryError } from 'src/queries/network.utils'
import {
  CircuitBreaker,
  createCircuitBreaker,
  EntityPusherSyncConfig,
} from 'src/queries/pusherEntitySync'
import { createRateLimiter, RateLimiter } from 'src/queries/pusherRateLimiter'
import { virtualCardsPaginatedQueryKeys } from 'src/queries/queries.constants'
import {
  CustomMutationOptions,
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getListQueryKeys,
  getNextExtendPageParam,
  MutationMethod,
  RequestConfig,
  useExtendInfiniteQuery,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'
import {
  PatchBulkVirtualCardUpdateResponse,
  PostBulkVirtualCardResponse,
  PutBulkVirtualCardCloseResponse,
  PutBulkVirtualCardDeactivateResponse,
  PutVirtualCardCancelResponse,
  PutVirtualCardCancelUpdateResponse,
  VirtualCardsDataModel,
  VirtualCardsFiltersBody,
  VirtualCardsResponse,
} from 'src/queries/virtualCards.models'
import {
  getVirtualCardDetail,
  getVirtualCards,
  patchBulkVirtualCardUpdate,
  postBulkVirtualCard,
  putBulkVirtualCardClose,
  putBulkVirtualCardDeactivate,
  putVirtualCardCancel,
  putVirtualCardCancelUpdate,
  resendVirtualCardCreatedEmail,
  ResendVirtualCardCreatedEmailResponse,
} from 'src/queries/virtualCards.network'
import {
  VIRTUAL_CARDS_QK,
  virtualCardsQueryKeys,
} from 'src/queries/virtualCards.queryKeys'
import {
  createSetIsBulkCloseVirtualCardsPopupOpen,
  createSetIsBulkDeactivateVirtualCardsPopupOpen,
  createSetIsBulkUpdateVirtualCardsFlyoutOpen,
} from 'src/redux/actions/app.actions'
import {
  GET_VIRTUAL_CARD_FAILURE,
  GET_VIRTUAL_CARD_INIT,
  GET_VIRTUAL_CARD_PERMISSIONS_FAILURE,
  GET_VIRTUAL_CARD_PERMISSIONS_INIT,
  GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS,
  GET_VIRTUAL_CARD_SUCCESS,
  getBulkVirtualCardJobStatus,
  GetVirtualCardSuccessPayload,
  PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE,
  PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT,
  PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS,
  POST_BULK_VIRTUAL_CARD_FAILURE,
  POST_BULK_VIRTUAL_CARD_INIT,
  POST_BULK_VIRTUAL_CARD_SUCCESS,
  PostBulkVirtualCardFailurePayload,
  PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE,
  PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT,
  PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_FAILURE,
  PUT_VIRTUAL_CARD_CANCEL_INIT,
  PUT_VIRTUAL_CARD_CANCEL_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS,
  PutBulkVirtualCardDeactivateFailurePayload,
} from 'src/redux/actions/data/virtualCards.actions'
import { GetVirtualCardsRequest } from 'src/requests/getVirtualCards.request'
import { PatchBulkVirtualCardUpdateRequest } from 'src/requests/patchBulkVirtualCardUpdate.request'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'
import { BackendErrorPayload } from 'src/utils/error.utils'

const selectVirtualCardSecureModel = (data: {
  virtualCard: VirtualCardSecureResponse
}): VirtualCardSecureModel => new VirtualCardSecureModel(data.virtualCard)

const selectVirtualCardPermissionsModel = (
  data: VirtualCardPermissionsResponse,
): VirtualCardPermissionsModel => new VirtualCardPermissionsModel(data)

export { virtualCardsQueryKeys } from 'src/queries/virtualCards.queryKeys'
export type {
  VirtualCardsDataModel,
  VirtualCardsFiltersBody,
  VirtualCardsResponse,
} from 'src/queries/virtualCards.models'

const selectVirtualCardListModel = (
  response: InfiniteData<VirtualCardsResponse, number>,
): VirtualCardsDataModel => ({
  virtualCards: response?.pages.flatMap((page) =>
    page.virtualCards.map((txn) => new VirtualCardListModel(txn)),
  ),
  page: response.pageParams.length - 1,
  totalItems: response?.pages[0]?.pagination.totalItems,
})

export const virtualCardQueries = {
  list: (filters: VirtualCardsFiltersBody | undefined, perPage = 50) =>
    infiniteQueryOptions({
      queryKey: virtualCardsQueryKeys.list(filters, { perPage }),
      queryFn: ({ pageParam }) =>
        getVirtualCards({ filters, page: pageParam, count: perPage }),
      enabled: filters !== undefined,
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        getNextExtendPageParam(lastPage.pagination),
      select: selectVirtualCardListModel,
      // Re-fetches happen in place without resetting back to an empty state.
      placeholderData: keepPreviousData,
      // Preserve useExtendInfiniteQuery's "do not replay all prior pages" behavior.
      gcTime: 0,
    }),
}

export type PostBulkVirtualCardVariables = {
  body: Partial<PostBulkVirtualCardRequest>
  // Threaded through to the SUCCESS payload's `invitingUsers` field (read by
  // VirtualCardsReducer.bulkJobs for the in-progress/complete toast copy) —
  // not sent over the wire.
  invitingUsers?: boolean
}

export type PatchBulkVirtualCardUpdateVariables = {
  body: PatchBulkVirtualCardUpdateRequest
}

export type PutBulkVirtualCardDeactivateVariables = {
  virtualCardIds: string[]
}

export type PutBulkVirtualCardCloseVariables = {
  virtualCardIds: string[]
}

/**
 * Recover the exact `err.response.data` the legacy VC-write reducers read
 * (BaseCreateVirtualCardReducer's `POST_BULK_VIRTUAL_CARD_FAILURE` shares this
 * shape with `POST_VIRTUAL_CARD_FAILURE`; `PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE`'s
 * `isHandledPaywall402` gate needs `status` + `exceptionType`;
 * DeactivateVirtualCardContentReducer's `parseBackendErrors` needs `error` +
 * `details`). `normalizeQueryError` stashes the raw AxiosError on the
 * non-enumerable `originalError`. Falls back to a generic message for a
 * non-axios / response-less failure (where the legacy thunks crashed reading
 * `err.response.data`). The wider shape (status always present, exceptionType
 * optional) is structurally assignable to the narrower deactivate/close/cancel
 * failure payload types too — shared by every bulk and single-card VC write
 * in this file.
 */
const recoverBackendErrorPayload = (
  errorSource: unknown,
): PostBulkVirtualCardFailurePayload => {
  if (axios.isAxiosError(errorSource) && errorSource.response) {
    return {
      ...(errorSource.response.data as BackendErrorPayload<string>),
      status: errorSource.response.status,
    }
  }
  // `error.originalError` is unset for a couple of normalizeQueryError's
  // branches (a string rejection, the total-fallback branch), in which case
  // the caller passes the already-normalized QueryError itself here — recover
  // its fields rather than falling through to the generic message below.
  if (
    errorSource !== null &&
    typeof errorSource === 'object' &&
    'error' in errorSource
  ) {
    const queryError = errorSource as QueryError
    return {
      error: queryError.error,
      errorCode: queryError.errorCode,
      details: queryError.details,
      status: queryError.status ?? 0,
    }
  }
  return {
    error: 'Sorry, something went wrong. Please try again later.',
    status: 0,
  }
}

// Narrow, list-shaped cache invalidation shared by every VC write below. A
// bulk op can touch many cards across any mounted list, so it invalidates
// every list-shaped VC query (a list key has an object at index 1) — but
// deliberately NOT the family root `virtualCardsQueryKeys.all` (which would
// also evict every card's detail/permissions/revisions cache and is the
// caching-incident (S1) refetch-storm anti-pattern) and NOT any single card's
// detail key (a bulk op does not know which individual detail caches are
// mounted; single-card writes invalidate their own detail key separately —
// see `useCancelVirtualCard` / `useCancelVirtualCardUpdate`). None of
// PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS / PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS
// / PUT_BULK_VIRTUAL_CARD_CLOSE_SUCCESS had a legacy `reactQueryInterop.reducer`
// case, so for the bulk writes this is net-new list coverage. The single-card
// cancel writes DO have a legacy `reactQueryInterop.reducer` case (it
// invalidates the family root off their preserved SUCCESS dispatch) — this
// call adds tighter, narrower coverage on top of that, not a reproduction of
// it. `usePostBulkVirtualCard` below reproduces the same list predicate
// inline (pre-existing, untouched by this slice).
export const invalidateVirtualCardLists = (
  queryClient: ReturnType<typeof useQueryClient>,
): void => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === VIRTUAL_CARDS_QK &&
      typeof query.queryKey[1] === 'object' &&
      query.queryKey[1] !== null,
  })
}

export type PutVirtualCardCancelVariables = {
  id: string
}

export type PutVirtualCardCancelUpdateVariables = {
  id: string
}

export type ResendVirtualCardEmailParams = {
  virtualCardId: string
}

export const virtualCardMutations = {
  bulkCreate: () =>
    mutationOptions<
      PostBulkVirtualCardResponse,
      QueryError,
      PostBulkVirtualCardVariables
    >({
      mutationFn: ({ body }) => postBulkVirtualCard(body),
    }),
  bulkUpdate: () =>
    mutationOptions<
      PatchBulkVirtualCardUpdateResponse,
      QueryError,
      PatchBulkVirtualCardUpdateVariables
    >({
      mutationFn: ({ body }) => patchBulkVirtualCardUpdate(body),
    }),
  bulkDeactivate: () =>
    mutationOptions<
      PutBulkVirtualCardDeactivateResponse,
      QueryError,
      PutBulkVirtualCardDeactivateVariables
    >({
      mutationFn: ({ virtualCardIds }) =>
        putBulkVirtualCardDeactivate(virtualCardIds),
    }),
  bulkClose: () =>
    mutationOptions<
      PutBulkVirtualCardCloseResponse,
      QueryError,
      PutBulkVirtualCardCloseVariables
    >({
      mutationFn: ({ virtualCardIds }) =>
        putBulkVirtualCardClose(virtualCardIds),
    }),
  cancel: () =>
    mutationOptions<
      PutVirtualCardCancelResponse,
      QueryError,
      PutVirtualCardCancelVariables
    >({
      mutationFn: ({ id }) => putVirtualCardCancel(id),
    }),
  cancelUpdate: () =>
    mutationOptions<
      PutVirtualCardCancelUpdateResponse,
      QueryError,
      PutVirtualCardCancelUpdateVariables
    >({
      mutationFn: ({ id }) => putVirtualCardCancelUpdate(id),
    }),
  resendCreatedEmail: () =>
    mutationOptions<
      ResendVirtualCardCreatedEmailResponse,
      QueryError,
      ResendVirtualCardEmailParams
    >({
      mutationFn: ({ virtualCardId }) =>
        resendVirtualCardCreatedEmail(virtualCardId),
    }),
}

/**
 * Create virtual cards in bulk (one or more recipients). Replaces the
 * `postBulkVirtualCardAndOpenToastAndCloseFlyout` thunk. Preserves its Redux
 * lifecycle dispatches (INIT/SUCCESS/FAILURE — SUCCESS/FAILURE still consumed
 * by VirtualCardsReducer.bulkJobs and BaseCreateVirtualCardReducer's shared
 * POST_BULK_VIRTUAL_CARD_FAILURE/POST_VIRTUAL_CARD_FAILURE case) and the
 * modal close, and adds a NARROW cache invalidation: only list-shaped VC
 * queries (a bulk push can add cards to any mounted list), never the
 * `virtualCardsQueryKeys.all` family root and never another card's
 * detail/permissions/revisions cache. The legacy `reactQueryInterop.reducer`
 * had no case for this action (bulk-created cards never invalidated the RQ
 * cache), so this list invalidation is net-new narrow coverage, not a
 * reproduction of a wider legacy invalidation.
 */
export const usePostBulkVirtualCard = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    PostBulkVirtualCardResponse,
    QueryError,
    PostBulkVirtualCardVariables
  >({
    ...virtualCardMutations.bulkCreate(),
    onMutate: () => {
      dispatch({ type: POST_BULK_VIRTUAL_CARD_INIT })
    },
    onSuccess: (data, variables) => {
      dispatch({
        type: POST_BULK_VIRTUAL_CARD_SUCCESS,
        payload: {
          ...data,
          displayName: variables.body.displayName,
          invitingUsers: variables.invitingUsers,
        },
      })
      dispatch(closeModal())
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === VIRTUAL_CARDS_QK &&
          typeof query.queryKey[1] === 'object' &&
          query.queryKey[1] !== null,
      })
    },
    onError: (error) => {
      dispatch({
        type: POST_BULK_VIRTUAL_CARD_FAILURE,
        payload: recoverBackendErrorPayload(error.originalError ?? error),
      })
    },
  })
}

/**
 * Bulk-updates virtual cards (notes/balance/validTo/recurrence). Replaces
 * `patchBulkVirtualCardUpdateAndCloseModal`. Preserves its Redux lifecycle
 * dispatches (INIT/SUCCESS/FAILURE — still consumed by
 * VirtualCardsAllCardsReducer for the `bulkUpdating` flag and the
 * `isHandledPaywall402`-gated error banner), the job-status poll dispatch,
 * clearing the card selection, and closing the flyout. Adds a narrow,
 * list-shaped cache invalidation (see `invalidateVirtualCardLists`).
 */
export const useBulkUpdateVirtualCards = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    PatchBulkVirtualCardUpdateResponse,
    QueryError,
    PatchBulkVirtualCardUpdateVariables
  >({
    ...virtualCardMutations.bulkUpdate(),
    onMutate: () => {
      dispatch({ type: PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT })
    },
    onSuccess: (data) => {
      dispatch({ type: PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS, payload: data })
      dispatch(getBulkVirtualCardJobStatus(data.bulkVirtualCardActionId))
      dispatch(createSaveSelectedVirtualCardIdsAction([]))
      dispatch(createSetIsBulkUpdateVirtualCardsFlyoutOpen(false))
      invalidateVirtualCardLists(queryClient)
    },
    onError: (error) => {
      dispatch({
        type: PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE,
        payload: recoverBackendErrorPayload(error.originalError ?? error),
      })
    },
  })
}

/**
 * Bulk-deactivates virtual cards. Replaces
 * `putBulkVirtualCardDeactivateAndCloseModal`. Preserves its Redux lifecycle
 * dispatches (INIT/SUCCESS/FAILURE — still consumed by
 * VirtualCardsAllCardsReducer for the `bulkDeactivating` flag, which also
 * gates BulkCloseVirtualCardsPopup's own action button), the job-status poll
 * dispatch, clearing the card selection, and closing the popup. Adds a
 * narrow, list-shaped cache invalidation (see `invalidateVirtualCardLists`).
 */
export const useBulkDeactivateVirtualCards = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    PutBulkVirtualCardDeactivateResponse,
    QueryError,
    PutBulkVirtualCardDeactivateVariables
  >({
    ...virtualCardMutations.bulkDeactivate(),
    onMutate: () => {
      dispatch({ type: PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT })
    },
    onSuccess: (data) => {
      dispatch({
        type: PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS,
        payload: data,
      })
      dispatch(getBulkVirtualCardJobStatus(data.bulkVirtualCardActionId))
      dispatch(createSaveSelectedVirtualCardIdsAction([]))
      dispatch(createSetIsBulkDeactivateVirtualCardsPopupOpen(false))
      invalidateVirtualCardLists(queryClient)
    },
    onError: (error) => {
      const payload: PutBulkVirtualCardDeactivateFailurePayload =
        recoverBackendErrorPayload(error.originalError ?? error)
      dispatch({ type: PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE, payload })
    },
  })
}

/**
 * Bulk-closes virtual cards. Replaces `putBulkVirtualCardCloseAndCloseModal`.
 * Its PUT_BULK_VIRTUAL_CARD_CLOSE_* actions had zero reducer consumers (no
 * loading flag, no error banner), so unlike update/deactivate there is no
 * Redux lifecycle dispatch to preserve here — only the job-status poll
 * dispatch, clearing the card selection, and closing the popup. Adds a
 * narrow, list-shaped cache invalidation (see `invalidateVirtualCardLists`).
 */
export const useBulkCloseVirtualCards = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    PutBulkVirtualCardCloseResponse,
    QueryError,
    PutBulkVirtualCardCloseVariables
  >({
    ...virtualCardMutations.bulkClose(),
    onSuccess: (data) => {
      dispatch(getBulkVirtualCardJobStatus(data.bulkVirtualCardActionId))
      dispatch(createSaveSelectedVirtualCardIdsAction([]))
      dispatch(createSetIsBulkCloseVirtualCardsPopupOpen(false))
      invalidateVirtualCardLists(queryClient)
    },
  })
}

/**
 * Cancels a virtual card (a pending request, or an active card). Replaces
 * `putVirtualCardCancelAndCloseModalOrRedirect`'s network call. Preserves its
 * Redux lifecycle dispatches (INIT/SUCCESS/FAILURE — still consumed by
 * DeactivateVirtualCardContent's and UpdateVirtualCardForm's own reducers for
 * their `puttingVirtualCardCancel` flags and error banner, VirtualCardsReducer's
 * cached card, and ReceiptAttachmentsReducer's attachment list). The thunk's
 * redirect-or-close(+toast) decision reads router state, which the
 * data-fetching layer must not own — it lives on
 * `closeOrRedirectAfterVirtualCardCancel` in virtualCards.actions.tsx, which
 * each call site dispatches after this mutation resolves. Adds a narrow
 * detail + list-shaped cache invalidation on top of whatever
 * `reactQueryInterop.reducer` already invalidates off the preserved
 * PUT_VIRTUAL_CARD_CANCEL_SUCCESS dispatch.
 */
export const useCancelVirtualCard = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    PutVirtualCardCancelResponse,
    QueryError,
    PutVirtualCardCancelVariables
  >({
    ...virtualCardMutations.cancel(),
    onMutate: () => {
      dispatch({ type: PUT_VIRTUAL_CARD_CANCEL_INIT })
    },
    onSuccess: (data, variables) => {
      dispatch({ type: PUT_VIRTUAL_CARD_CANCEL_SUCCESS, payload: data })
      // Belt-and-suspenders: reactQueryInterop.reducer's PUT_VIRTUAL_CARD_CANCEL_SUCCESS
      // case already invalidates the family root (a superset of these two
      // calls) off the dispatch above, so this narrow invalidation is
      // currently a no-op — kept so cache correctness doesn't regress the day
      // that dispatch (and its wide invalidation) is retired.
      queryClient.invalidateQueries({
        queryKey: virtualCardsQueryKeys.detail(variables.id),
      })
      invalidateVirtualCardLists(queryClient)
    },
    onError: (error) => {
      dispatch({
        type: PUT_VIRTUAL_CARD_CANCEL_FAILURE,
        payload: recoverBackendErrorPayload(error.originalError ?? error),
      })
    },
  })
}

/**
 * Cancels a pending virtual card update request. Replaces
 * `putVirtualCardCancelUpdateAndOpenToastAndCloseModal`'s network call.
 * Preserves its Redux lifecycle dispatches (INIT/SUCCESS/FAILURE — still
 * consumed by UpdateVirtualCardForm's own reducer for the
 * `puttingVirtualCardCancelUpdate` flag, and VirtualCardsReducer's cached
 * card). Its close(+toast) is left to the sole call site
 * (UpdateVirtualCardForm.tsx) to run after this mutation resolves. Adds a
 * narrow detail + list-shaped cache invalidation on top of whatever
 * `reactQueryInterop.reducer` already invalidates off the preserved
 * PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS dispatch.
 */
export const useCancelVirtualCardUpdate = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    PutVirtualCardCancelUpdateResponse,
    QueryError,
    PutVirtualCardCancelUpdateVariables
  >({
    ...virtualCardMutations.cancelUpdate(),
    onMutate: () => {
      dispatch({ type: PUT_VIRTUAL_CARD_CANCEL_UPDATE_INIT })
    },
    onSuccess: (data, variables) => {
      dispatch({ type: PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS, payload: data })
      // Belt-and-suspenders: reactQueryInterop.reducer's
      // PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS case already invalidates the
      // family root (a superset of these two calls) off the dispatch above,
      // so this narrow invalidation is currently a no-op — kept so cache
      // correctness doesn't regress the day that dispatch (and its wide
      // invalidation) is retired.
      queryClient.invalidateQueries({
        queryKey: virtualCardsQueryKeys.detail(variables.id),
      })
      invalidateVirtualCardLists(queryClient)
    },
    onError: (error) => {
      dispatch({
        type: PUT_VIRTUAL_CARD_CANCEL_UPDATE_FAILURE,
        payload: recoverBackendErrorPayload(error.originalError ?? error),
      })
    },
  })
}

// Fire-and-forget resend: it re-sends the card-created email and mutates no
// server state a query reads, so the hook wires NO invalidation. Success/limit/
// error toasts are component-specific (they depend on the recipient) and stay on
// the mutate(...) call site per the queries/AGENTS.md mutation rule.
export function useResendVirtualCardEmail(
  options?: CustomMutationOptions<
    ResendVirtualCardCreatedEmailResponse,
    QueryError,
    ResendVirtualCardEmailParams
  >,
) {
  return useMutation({
    ...virtualCardMutations.resendCreatedEmail(),
    ...options,
  })
}

interface QueryVirtualCardsOptions
  extends ExtendBaseInfiniteQueryOptions<
    VirtualCardsResponse,
    number,
    VirtualCardsDataModel
  > {
  filters: VirtualCardsFiltersBody | undefined
}

export const useQueryVirtualCards = ({
  filters,
  enabled = true,
  perPage = 50,
  ...rest
}: QueryVirtualCardsOptions) => {
  const { queryCacheOptions: _queryCacheOptions, ...queryOptionsRest } = rest
  const query = useInfiniteQuery({
    ...virtualCardQueries.list(filters, perPage),
    enabled: enabled && filters !== undefined,
    ...queryOptionsRest,
  })

  return {
    ...query,
    complete: !query.hasNextPage && !query.isFetching,
  }
}

interface QueryVirtualCardDetailOptions
  extends ExtendBaseQueryOptions<
    { virtualCard: VirtualCardSecureResponse },
    VirtualCardSecureModel
  > {
  virtualCardId: string | undefined
}

export const useQueryVirtualCardDetail = ({
  virtualCardId,
  enabled = true,
  // Single retry matches the legacy single-fetch behaviour and the 404-as-signal
  // contract on the detail surface; the default client retry of 3 would stall the
  // loading gate behind ~7s of backoff before NotFound / the empty-permissions
  // fallback renders.
  retry = 1,
  ...rest
}: QueryVirtualCardDetailOptions) => {
  const dispatch = useDispatch()

  return useExtendQuery<
    { virtualCard: VirtualCardSecureResponse },
    VirtualCardSecureModel
  >({
    queryKey: virtualCardsQueryKeys.detail(virtualCardId),
    queryUrl: () => `/virtualcards/${virtualCardId}`,
    enabled: enabled && virtualCardId !== undefined,
    select: selectVirtualCardSecureModel,
    requestConfig: RequestConfig.Vault,
    retry,
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_VIRTUAL_CARD_INIT },
    onSuccess: (data) => {
      // Tagged source: reactQueryInterop skips re-invalidating this key (untagged
      // dispatches invalidate — a loop off our own refetch).
      dispatch({
        type: GET_VIRTUAL_CARD_SUCCESS,
        payload: data,
        meta: { source: 'react-query' },
      })
    },
    onError: (error) => {
      dispatch({ type: GET_VIRTUAL_CARD_FAILURE, payload: error })
    },
  })
}

// Pusher → React Query sync (VirtualCard events). Rate-limit + Redux-parity
// policy: "Pusher → React Query sync" in src/queries/AGENTS.md.

/** Patch a single card into every cached VC list page by id. No refetch. */
export const updateVirtualCardListResponses = (
  queryClient: QueryClient,
  card: VirtualCardListResponse,
): void => {
  const listKeys = getListQueryKeys(queryClient, [VIRTUAL_CARDS_QK])
  listKeys.forEach((queryKey) => {
    queryClient.setQueryData(
      queryKey,
      (old: InfiniteData<VirtualCardsResponse, number> | undefined) => {
        if (!old?.pages) return old
        let changed = false
        const pages = old.pages.map((page) => {
          const index = page.virtualCards.findIndex((vc) => vc.id === card.id)
          if (index === -1) return page
          changed = true
          const virtualCards = [...page.virtualCards]
          // card is a superset of the list shape; overlay all fields, not just status.
          virtualCards[index] = { ...virtualCards[index], ...card }
          return { ...page, virtualCards }
        })
        // Keep referential identity for untouched lists.
        return changed ? { ...old, pages } : old
      },
    )
  })
}

/** Remove a card from every cached VC list page by id. No refetch. */
export const removeVirtualCardFromListResponses = (
  queryClient: QueryClient,
  virtualCardId: string,
): void => {
  const listKeys = getListQueryKeys(queryClient, [VIRTUAL_CARDS_QK])
  listKeys.forEach((queryKey) => {
    queryClient.setQueryData(
      queryKey,
      (old: InfiniteData<VirtualCardsResponse, number> | undefined) => {
        if (!old?.pages) return old
        let changed = false
        const pages = old.pages.map((page) => {
          if (!page.virtualCards.some((vc) => vc.id === virtualCardId))
            return page
          changed = true
          return {
            ...page,
            virtualCards: page.virtualCards.filter(
              (vc) => vc.id !== virtualCardId,
            ),
          }
        })
        return changed ? { ...old, pages } : old
      },
    )
  })
}

/** Apply a fetched card to the RQ detail + list caches and, for parity, the legacy Redux slice. */
export const applyVirtualCardUpdate = (
  queryClient: QueryClient,
  dispatch: Dispatch,
  card: VirtualCardSecureResponse,
): void => {
  queryClient.setQueryData<GetVirtualCardSuccessPayload>(
    virtualCardsQueryKeys.detail(card.id),
    { virtualCard: card },
  )
  updateVirtualCardListResponses(queryClient, card)
  // Tagged so reactQueryInterop skips re-invalidating detail(id).
  dispatch({
    type: GET_VIRTUAL_CARD_SUCCESS,
    payload: { virtualCard: card },
    meta: { source: 'react-query' },
  })
}

/** Mark VC caches stale without refetching now (eventual consistency). */
const markVirtualCardsStale = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({
    queryKey: virtualCardsQueryKeys.all,
    refetchType: 'none',
  })
}

const isRateLimitError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 429

// One budget per session, shared by every VC refresh consumer.
const virtualCardLimiter = createRateLimiter()
const virtualCardBreaker = createCircuitBreaker()

/** VirtualCard Pusher sync config: one limiter + breaker per session. */
export const createVirtualCardPusherSync = ({
  queryClient,
  dispatch,
  limiter = virtualCardLimiter,
  breaker = virtualCardBreaker,
}: {
  queryClient: QueryClient
  dispatch: Dispatch
  limiter?: RateLimiter
  breaker?: CircuitBreaker
}): EntityPusherSyncConfig<VirtualCardSecureResponse> => ({
  limiter,
  breaker,
  fetchDetail: getVirtualCardDetail,
  applyUpdate: (card) => applyVirtualCardUpdate(queryClient, dispatch, card),
  removeFromLists: (id) => removeVirtualCardFromListResponses(queryClient, id),
  // CREATED: mark lists stale — can't place a new card in a filtered,
  // paginated list without a server read.
  onCreate: () => markVirtualCardsStale(queryClient),
  onOverflow: () => markVirtualCardsStale(queryClient),
  isRateLimitError,
})

interface QueryVirtualCardPermissionsOptions
  extends ExtendBaseQueryOptions<
    VirtualCardPermissionsResponse,
    VirtualCardPermissionsModel
  > {
  virtualCardId: string | undefined
}

export const useQueryVirtualCardPermissions = ({
  virtualCardId,
  enabled = true,
  // See useQueryVirtualCardDetail: a single retry preserves the 404-as-signal
  // single-fetch behaviour instead of stalling the loading gate behind retries.
  retry = 1,
  ...rest
}: QueryVirtualCardPermissionsOptions) => {
  const dispatch = useDispatch()

  return useExtendQuery({
    queryKey: virtualCardsQueryKeys.permissions(virtualCardId),
    queryUrl: () => `/virtualcards/${virtualCardId}/permissions`,
    enabled: enabled && virtualCardId !== undefined,
    select: selectVirtualCardPermissionsModel,
    // TODO: we currently rely on the 404 error for the use-case of a user not having permission
    // and this is an anti-pattern against how react query usually handles error states
    // but this imitates behaviour today of making a single fetch when the user opens the flyout
    retry,
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_VIRTUAL_CARD_PERMISSIONS_INIT },
    onSuccess: (data) => {
      dispatch({ type: GET_VIRTUAL_CARD_PERMISSIONS_SUCCESS, payload: data })
    },
    onError: (error) => {
      dispatch({ type: GET_VIRTUAL_CARD_PERMISSIONS_FAILURE, payload: error })
    },
  })
}

export const usePostShareBillPayVirtualCard = () => {
  return useExtendMutation<
    { virtualCard: VirtualCardSecureResponse },
    { virtualCardId: string; body: { vendorEmail: string } }
  >(MutationMethod.POST, {
    mutationUrl: ({ virtualCardId }) => `/virtualcards/${virtualCardId}/share`,
  })
}

export const usePutVirtualCardSupportCode = () => {
  return useExtendMutation<
    { customerSupportCode: number },
    { virtualCardId: string; body: Record<string, never> }
  >(MutationMethod.PUT, {
    mutationUrl: ({ virtualCardId }) =>
      `/virtualcards/${virtualCardId}/supportcode`,
  })
}

interface VirtualCardRevisionsRespones {
  revisions: VirtualCardRevisionListResponse[]
  pagination: GetVirtualCardRevisionsPagination
}

const selectVirtualCardRevisionListModel = (
  response: InfiniteData<VirtualCardRevisionsRespones, string | undefined>,
  cardType: VirtualCardType | undefined,
  vendorName: string | undefined,
) => ({
  revisions: response?.pages.flatMap((page) =>
    page.revisions.map(
      (vcr) => new VirtualCardRevisionListModel(vcr, cardType, vendorName),
    ),
  ),
})

interface QueryVirtualCardRevisionsOptions
  extends ExtendBaseInfiniteQueryOptions<
    VirtualCardRevisionsRespones,
    string | undefined,
    { revisions: VirtualCardRevisionListModel[] }
  > {
  virtualCardId: string | undefined
  queryParams: GetVirtualCardRevisionsQueryParams | undefined
}

interface QueryVirtualCardRevisionsSelectorOptions {
  cardType?: VirtualCardType
  vendorName?: string
}

// react query example demonstrating a custom implementation of a non-numerical page param
// also shows separation of concerns for options passed to the network query request
// and options that are used to build the model for the front-end
export const useQueryVirtualCardRevisions = (
  {
    virtualCardId,
    queryParams,
    enabled = true,
    ...rest
  }: QueryVirtualCardRevisionsOptions,
  { cardType, vendorName }: QueryVirtualCardRevisionsSelectorOptions,
) => {
  return useExtendInfiniteQuery<
    VirtualCardRevisionsRespones,
    { revisions: VirtualCardRevisionListModel[] },
    QueryKey,
    string | undefined
  >({
    queryKey: virtualCardsQueryKeys.revisions(virtualCardId, queryParams),
    queryUrl: ({ pageParam }) => {
      return `virtualcards/${virtualCardId}/revisions?${queryString.stringify({ ...queryParams, nextCursor: pageParam })}`
    },
    enabled:
      enabled && virtualCardId !== undefined && queryParams !== undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    getPreviousPageParam: (firstPage, _, __) =>
      firstPage.pagination.previousCursor,
    select: (response) =>
      selectVirtualCardRevisionListModel(response, cardType, vendorName),
    initialPageParam: undefined,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...rest,
  })
}

interface QueryVirtualCardsPaginatedOptions
  extends ExtendBaseQueryOptions<VirtualCardsResponse, VirtualCardsDataModel> {
  filters: GetVirtualCardsRequest | undefined
}

export const useQueryVirtualCardsPaginated = ({
  filters,
  enabled = true,
  ...rest
}: QueryVirtualCardsPaginatedOptions) => {
  return useExtendQuery({
    queryKey: virtualCardsPaginatedQueryKeys.list(filters),
    queryUrl: () => `virtualcards?${queryString.stringify({ ...filters })}`,
    enabled: enabled && filters !== undefined,
    select: (data: VirtualCardsResponse) => ({
      virtualCards: data.virtualCards.map(
        (vcn) => new VirtualCardListModel(vcn),
      ),
      page: data.pagination.page,
      totalItems: data.pagination.totalItems,
    }),
    ...rest,
  })
}
